import {
    clearMsalCacheArtifacts,
    clearStoredMsalSessionHint,
} from "@/services/msalSessionState";
import { clearStoredExternalUserProfile } from "@/services/webSessionStorage";
import { clearStoredInternalUserProfile } from "@/services/internalSessionProfileStorage";
import { clearTokenData } from "@/services/tokenStorage";

export const ACCOUNT_ACCESS_STATE_KEY = "app_access_denied_reason";
export const ACCOUNT_INACTIVE_REASON = "account_inactive";
export const ACCOUNT_ACCESS_REVOKED_EVENT = "dispatchiq:account-access-revoked";
export const AUTH_REDIRECT_NOTICE_KEY = "app_auth_redirect_notice";
export const LOGOUT_EVENT_KEY = "app_logout_event";
const AUTH_STATE_KEY = "app_auth_state";
const DISPLAY_LOCK_KEY = "timecard_display_lock";
const DISPLAY_LOCK_HEARTBEAT_KEY = "timecard_display_lock_heartbeat";

export type AccountAccessState = {
    reason: typeof ACCOUNT_INACTIVE_REASON;
    code: "ACCOUNT_INACTIVE";
    message: string;
    timestamp: number;
    redirectPath?: string;
};

export type AuthRedirectNotice = {
    reason: typeof ACCOUNT_INACTIVE_REASON;
    message: string;
    timestamp: number;
    redirectPath: string;
};

const normalizeInactiveMessage = (message?: string) =>
    typeof message === "string" && message.trim()
        ? message
        : "Your account is inactive. Please contact your administrator.";

const parseStoredState = (value: string | null): AccountAccessState | null => {
    if (!value) {
        return null;
    }

    try {
        const parsed = JSON.parse(value) as Partial<AccountAccessState>;
        if (
            parsed?.reason !== ACCOUNT_INACTIVE_REASON ||
            parsed.code !== "ACCOUNT_INACTIVE" ||
            typeof parsed.timestamp !== "number"
        ) {
            return null;
        }

        return {
            reason: parsed.reason,
            code: parsed.code,
            message: normalizeInactiveMessage(parsed.message),
            timestamp: parsed.timestamp,
            redirectPath:
                typeof parsed.redirectPath === "string" &&
                parsed.redirectPath.trim()
                    ? parsed.redirectPath
                    : undefined,
        };
    } catch {
        return null;
    }
};

const parseRedirectNotice = (
    value: string | null
): AuthRedirectNotice | null => {
    if (!value) {
        return null;
    }

    try {
        const parsed = JSON.parse(value) as Partial<AuthRedirectNotice>;
        if (
            parsed?.reason !== ACCOUNT_INACTIVE_REASON ||
            typeof parsed.timestamp !== "number" ||
            typeof parsed.redirectPath !== "string" ||
            !parsed.redirectPath.trim()
        ) {
            return null;
        }

        return {
            reason: parsed.reason,
            message: normalizeInactiveMessage(parsed.message),
            timestamp: parsed.timestamp,
            redirectPath: parsed.redirectPath,
        };
    } catch {
        return null;
    }
};

const clearClientAuthArtifacts = (): void => {
    clearTokenData();
    clearStoredExternalUserProfile();
    clearStoredInternalUserProfile();
    clearStoredMsalSessionHint();
    clearMsalCacheArtifacts(window.localStorage);
    clearMsalCacheArtifacts(window.sessionStorage);
    window.sessionStorage.removeItem("auth_in_progress");
    localStorage.removeItem(AUTH_STATE_KEY);
    localStorage.removeItem(DISPLAY_LOCK_KEY);
    localStorage.removeItem(DISPLAY_LOCK_HEARTBEAT_KEY);
};

const broadcastLogoutEvent = (
    state: AccountAccessState,
    redirectPath?: string
): void => {
    localStorage.setItem(
        LOGOUT_EVENT_KEY,
        JSON.stringify({
            timestamp: state.timestamp,
            reason: state.reason,
            code: state.code,
            message: state.message,
            redirectPath,
        })
    );
    window.dispatchEvent(
        new CustomEvent<AccountAccessState>(ACCOUNT_ACCESS_REVOKED_EVENT, {
            detail: {
                ...state,
                redirectPath,
            },
        })
    );
};

export function readStoredAccountAccessState(): AccountAccessState | null {
    if (typeof window === "undefined") {
        return null;
    }

    return parseStoredState(localStorage.getItem(ACCOUNT_ACCESS_STATE_KEY));
}

export function clearStoredAccountAccessState(): void {
    if (typeof window === "undefined") {
        return;
    }

    localStorage.removeItem(ACCOUNT_ACCESS_STATE_KEY);
}

export function readStoredAuthRedirectNotice(): AuthRedirectNotice | null {
    if (typeof window === "undefined") {
        return null;
    }

    return parseRedirectNotice(localStorage.getItem(AUTH_REDIRECT_NOTICE_KEY));
}

export function clearStoredAuthRedirectNotice(): void {
    if (typeof window === "undefined") {
        return;
    }

    localStorage.removeItem(AUTH_REDIRECT_NOTICE_KEY);
}

export function hasStoredLogoutEvent(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    return Boolean(localStorage.getItem(LOGOUT_EVENT_KEY));
}

export function clearStoredLogoutEvent(): void {
    if (typeof window === "undefined") {
        return;
    }

    localStorage.removeItem(LOGOUT_EVENT_KEY);
}

export function consumeStoredAuthRedirectNotice(
    expectedRedirectPath?: string
): AuthRedirectNotice | null {
    if (typeof window === "undefined") {
        return null;
    }

    const notice = readStoredAuthRedirectNotice();
    if (!notice) {
        return null;
    }

    if (expectedRedirectPath && notice.redirectPath !== expectedRedirectPath) {
        return null;
    }

    clearStoredAuthRedirectNotice();
    return notice;
}

export function applyAccountInactiveClientState(message?: string): void {
    if (typeof window === "undefined") {
        return;
    }

    const state: AccountAccessState = {
        reason: ACCOUNT_INACTIVE_REASON,
        code: "ACCOUNT_INACTIVE",
        message: normalizeInactiveMessage(message),
        timestamp: Date.now(),
        redirectPath: "/403",
    };

    clearClientAuthArtifacts();
    clearStoredAuthRedirectNotice();
    localStorage.setItem(ACCOUNT_ACCESS_STATE_KEY, JSON.stringify(state));
    broadcastLogoutEvent(state, "/403");
}

export function applySessionAccountInactiveClientState(args?: {
    message?: string;
    redirectPath?: string;
}): void {
    if (typeof window === "undefined") {
        return;
    }

    const redirectPath =
        typeof args?.redirectPath === "string" && args.redirectPath.trim()
            ? args.redirectPath
            : "/";
    const state: AccountAccessState = {
        reason: ACCOUNT_INACTIVE_REASON,
        code: "ACCOUNT_INACTIVE",
        message: normalizeInactiveMessage(args?.message),
        timestamp: Date.now(),
        redirectPath,
    };

    clearClientAuthArtifacts();
    localStorage.removeItem(ACCOUNT_ACCESS_STATE_KEY);
    localStorage.setItem(
        AUTH_REDIRECT_NOTICE_KEY,
        JSON.stringify({
            reason: state.reason,
            message: state.message,
            timestamp: state.timestamp,
            redirectPath,
        } satisfies AuthRedirectNotice)
    );
    broadcastLogoutEvent(state, redirectPath);
}
